/* eslint-disable react-hooks/exhaustive-deps */

import React, { FC, useContext, useMemo } from 'react';
import { SearchContext } from '../SearchContext';
import { Heading4, Divider } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import {
    SeoLinksWrapper,
    TownsLinkText,
    CurrentlyAvailableLinkText,
    SeoLinksSection,
    SeoLinksInnerWrapper
} from './SeoLinks.styles';
import Link from 'next/link';

import { SeoLinks, Row, SeoLink } from './SeoLinks.types';

const titleText = (type: string, geo: string): string => {
    switch (type) {
        case 'sale':
            return `Currently available for sale in ${geo}`;
        case 'rent':
            return `Currently available to rent in ${geo}`;
        default:
            return '';
    }
};

const getSEOHeading = (currentlyAvailable: SeoLinks[]) => {
    const newHomesSEOHeading = `Currently available for sale in ${currentlyAvailable[0]?.geo}`;

    const searchResultsPageSEOHeading = titleText(
        currentlyAvailable[0]?.transactionType,
        currentlyAvailable[0]?.geo
    );

    return currentlyAvailable[0]?.section === 'new-homes'
        ? newHomesSEOHeading
        : searchResultsPageSEOHeading;
};

export const SeoLinksComponent: FC = () => {
    const { state } = useContext(SearchContext);

    const seoLinksData = state.seoAccordions;

    const currentlyAvailable = seoLinksData.filter(
        (accordion: SeoLinks) => accordion.name === 'currentlyAvailable'
    );

    const townsAndCities = seoLinksData.filter(
        (accordion: SeoLinks) => accordion.name === 'townsAndCities'
    );
    const townsAndCitiesObj = townsAndCities[0];
    const currentlyAvailableObj = currentlyAvailable[0];

    const formatCurrentlyAvailable = (propertyType: string) => {
        const typeLinks = currentlyAvailableObj?.rows?.map((row: Row) => {
            return (
                row.links &&
                row.links.filter(
                    (link: SeoLink) => link.propertyType === propertyType
                )
            );
        });

        const formatLinkSection = (section: string) =>
            section.replace('new-homes', 'for-sale').replace('-', ' ');

        return (
            <>
                <Divider variant="horizontal" />
                <SeoLinksInnerWrapper>
                    {typeLinks &&
                        typeLinks.map((links: SeoLink[]) => {
                            return (
                                links &&
                                links.map(
                                    (
                                        {
                                            section,
                                            uri,
                                            propertyType,
                                            geo
                                        }: SeoLink,
                                        index: number
                                    ) => {
                                        let linkText = '';

                                        switch (true) {
                                            case uri.indexOf('/1-') > -1:
                                                linkText = `1 bedroom ${propertyType} ${formatLinkSection(
                                                    section
                                                )}`;
                                                break;
                                            case uri.indexOf('/2-') > -1:
                                                linkText = `2 bedroom ${propertyType} ${formatLinkSection(
                                                    section
                                                )}`;
                                                break;
                                            case uri.indexOf('/3-') > -1:
                                                linkText = `3 bedroom ${propertyType} ${formatLinkSection(
                                                    section
                                                )}`;
                                                break;
                                            case uri.indexOf('/4-') > -1:
                                                linkText = `4 bedroom ${propertyType} ${formatLinkSection(
                                                    section
                                                )}`;
                                                break;
                                            default:
                                                linkText = `All ${propertyType} ${formatLinkSection(
                                                    section
                                                )} in ${geo}`;
                                        }

                                        return (
                                            <Link
                                                href={uri}
                                                key={geo + index}
                                                passHref
                                            >
                                                <CurrentlyAvailableLinkText
                                                    variant="tertiary"
                                                    data-testid="seo-currently-available-links"
                                                    onClick={() =>
                                                        triggerTracking({
                                                            event: 'GAEvent',
                                                            eventCategory:
                                                                'search results',
                                                            eventAction:
                                                                'SEO widget click',
                                                            eventLabel: linkText
                                                        })
                                                    }
                                                    tabIndex={0}
                                                >
                                                    {linkText}
                                                </CurrentlyAvailableLinkText>
                                            </Link>
                                        );
                                    }
                                )
                            );
                        })}
                </SeoLinksInnerWrapper>
            </>
        );
    };

    const formatTownAndCities = () => (
        <>
            <Divider variant="horizontal" />
            <SeoLinksInnerWrapper>
                {townsAndCitiesObj?.links?.map(
                    (link: SeoLink, index: number) => {
                        return (
                            <Link
                                href={link.uri}
                                key={link.geo + index}
                                passHref
                            >
                                <TownsLinkText
                                    variant="tertiary"
                                    data-testid="seo-town-links"
                                    onClick={() =>
                                        triggerTracking({
                                            event: 'GAEvent',
                                            eventCategory: 'search results',
                                            eventAction: 'SEO widget click',
                                            eventLabel: link.geo
                                        })
                                    }
                                    tabIndex={0}
                                >
                                    {link.geo}
                                </TownsLinkText>
                            </Link>
                        );
                    }
                )}
            </SeoLinksInnerWrapper>
        </>
    );
    const seoHeading = getSEOHeading(currentlyAvailable);
    return useMemo(
        () => (
            <SeoLinksWrapper data-testid="seo-links">
                {currentlyAvailable.length > 0 && (
                    <SeoLinksSection>
                        <SeoLinksInnerWrapper>
                            <Heading4 size="8" color={'text-title'}>
                                {seoHeading}
                            </Heading4>
                        </SeoLinksInnerWrapper>

                        {formatCurrentlyAvailable('houses')}
                        {formatCurrentlyAvailable('flats')}
                        {formatCurrentlyAvailable('property')}
                        {formatCurrentlyAvailable('bungalows')}
                        {formatCurrentlyAvailable('studios')}
                    </SeoLinksSection>
                )}

                {townsAndCities.length > 0 && (
                    <SeoLinksSection>
                        <SeoLinksInnerWrapper>
                            <Heading4 size="8" color={'text-title'}>
                                Towns and cities within {townsAndCities[0]?.geo}
                            </Heading4>
                        </SeoLinksInnerWrapper>

                        {formatTownAndCities()}
                    </SeoLinksSection>
                )}
            </SeoLinksWrapper>
        ),
        []
    );
};
